import { useState } from "react";
import { Check, X, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import AdminListToolbar from "../../components/admin/AdminListToolbar";
import AdminPagination from "../../components/admin/AdminPagination";
import AdminKycLogsPanel from "../../components/admin/AdminKycLogsPanel";

export default function AdminKycTab({
  pendingKyc,
  kycSearchDraft,
  onKycSearchDraftChange,
  onKycSearch,
  kycTotal,
  page,
  totalPages,
  onPageChange,
  onApprove,
  onReject,
}) {
  const [dobByUser, setDobByUser] = useState({});

  const handleApprove = async (u) => {
    const k = u.last_kyc || {};
    const hasDob = !!(u.date_of_birth || k.id_dob);
    const entered = (dobByUser[u.user_id] || "").trim();
    if (!hasDob && !entered) {
      toast.error("Enter date of birth before approving");
      return;
    }
    try {
      await onApprove(u.user_id, hasDob ? undefined : entered);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail === "date_of_birth_required") {
        toast.error("Enter date of birth before approving");
      } else if (detail === "renter_underage") {
        toast.error("Renter must be at least 24 years old");
      } else if (detail === "invalid_date_of_birth") {
        toast.error("Enter a valid date of birth");
      } else {
        toast.error(detail || "Could not approve KYC");
      }
    }
  };

  return (
    <>
      <div className="zc-card overflow-hidden overflow-x-auto mt-4" data-testid="admin-pending-kyc">
        <AdminListToolbar
          draft={kycSearchDraft}
          onDraftChange={onKycSearchDraftChange}
          onSearch={onKycSearch}
          placeholder="Search by name, email, user ID…"
          testid="admin-kyc-search"
          total={kycTotal}
          shown={pendingKyc.length}
        />
        <div className="px-4 sm:px-6 py-3 border-b border-[var(--zc-border)] eyebrow">Manual review queue</div>
        {pendingKyc.length === 0 && <div className="px-4 sm:px-6 py-10 text-center text-[var(--zc-text-3)] text-sm">No KYC reviews waiting.</div>}
        {pendingKyc.map(u => {
          const k = u.last_kyc || {};
          const score = (typeof k.liveness_score === "number") ? Math.round(k.liveness_score * 100) : null;
          const hasDob = !!(u.date_of_birth || k.id_dob);
          return (
            <div key={u.user_id} className="flex flex-col gap-3 sm:grid sm:grid-cols-[40px,1fr,auto] sm:items-start px-4 sm:px-6 py-4 border-b border-[var(--zc-border)] last:border-0">
              <div className="w-10 h-10 rounded-full grid place-items-center shrink-0" style={{ background: "rgba(245,158,11,0.12)" }}>
                <ShieldCheck size={16} style={{ color: "var(--zc-warn,#f59e0b)" }} />
              </div>
              <div className="min-w-0">
                <div className="zc-title text-lg leading-tight">{u.name || "—"} <span className="text-[var(--zc-text-3)] text-xs">· {u.email}</span></div>
                <div className="text-xs text-[var(--zc-text-3)] mt-1">
                  Didit: <b className="text-[var(--zc-text-2)]">{k.id_status || "—"}</b>
                  {score !== null && <> · Liveness <b className="text-[var(--zc-text-2)]">{score}%</b></>}
                </div>
                {k.review_reason && <div className="text-xs mt-1" style={{ color: "var(--zc-warn,#f59e0b)" }}>⚠ {k.review_reason}</div>}
                {(k.id_first_name || k.id_last_name) && (
                  <div className="text-xs text-[var(--zc-text-3)] mt-1 font-mono break-all">
                    {k.id_first_name} {k.id_last_name} · {k.id_document_type || "ID"} {k.id_document_number ? `#${k.id_document_number}` : ""} · DOB {k.id_dob || u.date_of_birth || "—"}
                  </div>
                )}
                {Array.isArray(k.id_warnings) && k.id_warnings.length > 0 && (
                  <div className="text-xs text-[var(--zc-text-3)] mt-1">Warnings: {k.id_warnings.join(", ")}</div>
                )}
                {!hasDob && (
                  <label className="block mt-2 max-w-xs" htmlFor={`kyc-dob-${u.user_id}`}>
                    <span className="text-xs text-[var(--zc-text-3)] block mb-1">Date of birth (required to approve)</span>
                    <input
                      id={`kyc-dob-${u.user_id}`}
                      type="date"
                      className="zc-input h-9 px-3 w-full text-sm"
                      value={dobByUser[u.user_id] || ""}
                      onChange={(e) => setDobByUser((prev) => ({ ...prev, [u.user_id]: e.target.value }))}
                      data-testid={`kyc-dob-${u.user_id}`}
                    />
                  </label>
                )}
              </div>
              <div className="flex flex-wrap gap-2 shrink-0 zc-stack-actions">
                <button onClick={() => handleApprove(u)} className="px-3 h-9 zc-btn-primary text-xs flex items-center gap-1" data-testid={`kyc-approve-${u.user_id}`}>
                  <Check size={12} /> Approve
                </button>
                <button onClick={() => onReject(u.user_id)} className="px-3 h-9 zc-btn-ghost text-xs flex items-center gap-1" data-testid={`kyc-reject-${u.user_id}`}>
                  <X size={12} /> Reject
                </button>
              </div>
            </div>
          );
        })}
        <AdminPagination page={page} totalPages={totalPages} onPageChange={onPageChange} testid="admin-kyc-pagination" />
      </div>
      <AdminKycLogsPanel />
    </>
  );
}
